import { useState } from "react";
import { Globe2, Sparkles, CheckCircle2, Loader2, ArrowLeft, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import ScrollReveal from "@/components/ScrollReveal";
import CountryCombobox from "@/components/CountryCombobox";

type Step = "details" | "verify" | "done";

export default function GlobalLaunchWaitlist() {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email ?? "");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<Step>("details");
  const [loading, setLoading] = useState(false);

  const sendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (!country) {
      toast.error("Please choose your country first.");
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("waitlist-otp", {
        body: { action: "send", email: cleanEmail, name: name.trim() || null, country },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success(`We sent a 6-digit code to ${cleanEmail}`);
      setStep("verify");
    } catch (err: any) {
      const raw = err?.message ?? err?.context?.error ?? "";
      const friendly =
        (err?.context?.message as string | undefined) ??
        (raw.includes("too_many_ip_requests")
          ? "Too many signups from this network — please try again later."
          : raw.includes("too_many_requests")
            ? "You've requested too many codes. Please wait ~10 minutes."
            : raw.includes("invalid_email")
              ? "That email looks invalid."
              : raw.includes("country_required")
                ? "Please choose your country first."
                : raw.includes("email_send_failed")
                  ? "We couldn't send the email — please try again in a moment."
                  : "Could not send code. Please try again.");
      toast.error(friendly);
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (code.length !== 6) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("waitlist-otp", {
        body: { action: "verify", email: email.trim().toLowerCase(), code },
      });
      if (error) throw error;
      const payload = data as any;
      if (payload?.error) throw new Error(payload.error);
      toast.success("Email confirmed — you're on the list.");
      setStep("done");
    } catch (err: any) {
      const raw = err?.message ?? err?.context?.error ?? "";
      const remaining = err?.context?.attemptsRemaining;
      if (raw.includes("incorrect_code"))
        toast.error(
          typeof remaining === "number"
            ? `That code is incorrect. ${remaining} attempt${remaining === 1 ? "" : "s"} left.`
            : "That code is incorrect.",
        );
      else if (raw.includes("code_expired")) toast.error("Code expired — request a new one.");
      else if (raw.includes("too_many_attempts")) toast.error("Too many attempts. Request a new code.");
      else if (raw.includes("no_pending_code")) toast.error("No pending code — send one first.");
      else if (raw.includes("invalid_code_format")) toast.error("Verification codes are 6 digits.");
      else toast.error("Could not verify code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="global-waitlist" className="scroll-mt-24 py-24 border-t border-border/50">
      <div className="container mx-auto px-6">
        <ScrollReveal>
          <div className="mx-auto max-w-3xl rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/10 via-background to-accent/10 p-8 md:p-12 shadow-2xl">
            <div className="text-center">
              <Badge className="mb-4 rounded-full bg-primary/15 text-primary border-primary/30 gap-1.5 px-3 py-1.5">
                <Globe2 className="h-3.5 w-3.5" /> Global Launch
              </Badge>
              <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight text-foreground">
                Launching <span className="gradient-text">worldwide</span> — join your country's waitlist
              </h2>
              <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
                Pick your country and be first in line for WeKIT™ Career Clarity 360 —
                we'll email you the moment we launch there, with early-access pricing.
              </p>
            </div>

            {step === "done" ? (
              <div className="mt-8 text-center">
                <CheckCircle2 className="mx-auto h-12 w-12 text-primary" />
                <p className="mt-3 font-display text-xl font-bold">You're on the list</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  We'll email you the moment we launch in {country}.
                </p>
              </div>
            ) : step === "verify" ? (
              <div className="mt-8 flex flex-col items-center gap-5">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <ShieldCheck className="h-4 w-4 text-primary" />
                  Enter the 6-digit code we sent to <span className="font-medium text-foreground">{email}</span>
                </div>
                <InputOTP maxLength={6} value={code} onChange={setCode} autoFocus>
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} className="h-12 w-12 text-lg" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
                <Button
                  size="lg"
                  className="h-12 rounded-full px-8 font-semibold hover-glow"
                  disabled={loading || code.length !== 6}
                  onClick={verifyCode}
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm email"}
                </Button>
                <div className="flex items-center gap-3 text-xs">
                  <button
                    type="button"
                    onClick={() => { setStep("details"); setCode(""); }}
                    className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                  >
                    <ArrowLeft className="h-3 w-3" /> Use a different email
                  </button>
                  <span className="text-muted-foreground/50">•</span>
                  <button
                    type="button"
                    onClick={(e) => sendCode(e as any)}
                    className="text-primary hover:underline disabled:opacity-50"
                    disabled={loading}
                  >
                    Resend code
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={sendCode} className="mt-8 grid gap-3 sm:grid-cols-2">
                <Input
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                  className="h-12 rounded-full bg-background/60 px-5"
                />
                <CountryCombobox value={country} onChange={setCountry} />
                <Input
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  maxLength={255}
                  className="h-12 rounded-full bg-background/60 px-5 sm:col-span-2"
                />
                <Button
                  type="submit"
                  size="lg"
                  disabled={loading || !email}
                  className="h-12 rounded-full sm:col-span-2 font-semibold hover-glow"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Send me a verification code
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground text-center sm:col-span-2">
                  We'll email a 6-digit code to confirm your address. No spam.
                </p>
              </form>
            )}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
